import {
  getObfuscatedLocalStorage,
  setObfuscatedLocalStorage,
} from "@/lib/storage";
import type { PaymentLink } from "../types";
import type {
  FailedPaymentReturnData,
  PendingPaymentData,
  RecreatedCartReturnData,
} from "../types/local-storage.type";
import type { Order } from "../types/order.type";
import type { Payment } from "../types/order-payment.type";

const pendingPaymentStorageKey = "pendingPayment";
const failedPaymentReturnStorageKey = "failedPaymentReturn";
const recreatedCartReturnStorageKey = "recreatedCartReturn";
export const pendingPaymentEventName = "pending-payment-change";
export const failedPaymentReturnEventName = "failed-payment-return-change";
export const recreatedCartReturnEventName = "recreated-cart-return-change";

const emitPendingPaymentChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(pendingPaymentEventName));
};

const emitFailedPaymentReturnChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(failedPaymentReturnEventName));
};

const emitRecreatedCartReturnChange = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(recreatedCartReturnEventName));
};

export const getPendingPayment = (): PendingPaymentData | null => {
  return getObfuscatedLocalStorage<PendingPaymentData>(
    pendingPaymentStorageKey,
  );
};

export const setPendingPayment = ({
  cartType,
  orderId,
  paymentId,
}: {
  cartType: Order["type"];
  orderId: string;
  paymentId: string;
}) => {
  setObfuscatedLocalStorage<PendingPaymentData>(pendingPaymentStorageKey, {
    cartType,
    createdAt: Date.now(),
    orderId,
    paymentId,
  });

  emitPendingPaymentChange();
};

export const setPendingPaymentFromLink = ({
  cartType,
  orderId,
  paymentLink,
}: {
  cartType: Order["type"];
  orderId: string;
  paymentLink: PaymentLink;
}) => {
  setPendingPayment({
    cartType,
    orderId,
    paymentId: paymentLink.id,
  });
};

export const clearPendingPayment = () => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(pendingPaymentStorageKey);
  emitPendingPaymentChange();
};

export const getFailedPaymentReturn = (): FailedPaymentReturnData | null => {
  return getObfuscatedLocalStorage<FailedPaymentReturnData>(
    failedPaymentReturnStorageKey,
  );
};

export const setFailedPaymentReturn = ({
  cartType,
  orderId,
  paymentStatus,
}: {
  cartType: Order["type"];
  orderId: string;
  paymentStatus: FailedPaymentReturnData["paymentStatus"];
}) => {
  setObfuscatedLocalStorage<FailedPaymentReturnData>(
    failedPaymentReturnStorageKey,
    {
      cartType,
      createdAt: Date.now(),
      orderId,
      paymentStatus,
    },
  );

  emitFailedPaymentReturnChange();
};

export const clearFailedPaymentReturn = () => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(failedPaymentReturnStorageKey);
  emitFailedPaymentReturnChange();
};

export const getRecreatedCartReturn = (): RecreatedCartReturnData | null => {
  return getObfuscatedLocalStorage<RecreatedCartReturnData>(
    recreatedCartReturnStorageKey,
  );
};

export const setRecreatedCartReturn = ({
  flowType,
}: {
  flowType: RecreatedCartReturnData["flowType"];
}) => {
  setObfuscatedLocalStorage<RecreatedCartReturnData>(
    recreatedCartReturnStorageKey,
    {
      createdAt: Date.now(),
      flowType,
    },
  );

  emitRecreatedCartReturnChange();
};

export const clearRecreatedCartReturn = () => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(recreatedCartReturnStorageKey);
  emitRecreatedCartReturnChange();
};

export const isTerminalPaymentStatus = (
  status: Payment["status"],
): status is Extract<
  Payment["status"],
  "COMPLETED" | "CANCELED" | "REJECTED"
> => {
  return (
    status === "COMPLETED" || status === "CANCELED" || status === "REJECTED"
  );
};
